import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import strings from '../../constant/strings';
import { getPreorderProductApi, getPreorderProductsApi } from '../../services/preorderService';
import {
  Card, CenterState, DispatchBadge, Gallery, Hero, Highlights, Muted, Page, PlanAmount,
  PlanCard, PlanList, PlanTitle, PlanTop, Price, PrimaryButton, SaveTag, SectionTitle,
  Title, Notice, OutlineButton, ProductPicker, ProductTab,
  GalleryThumb, ConsentRow, ConsentTag,
  BuyBox, FaqItem, FeatureRow, GalleryPane, Mrp, QtyBox, SectionHead,
  PosterGrid, StickyBuyBar, StockPill, TagGrid, TagItem, Tagline, ThumbRail,
  VideoFrame, ProductClass, ToTopButton,
  TrustRow, ValueCard, ValueStrip,
  CrossSell, PackageCard, PackageGrid, PackageSummary,
} from './styles';
import { demoFor } from './demoContent';
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import {
  CONSENT_COPY, PLAN_COPY, clearDraft, clearIntent, formatPaise, formatWindow,
  markJustPlaced, readAttribution, readDraft, readIntent, saveDraft, saveIntent, scrollPageToTop,
} from './utils';
import { setReturnTo } from '../../utils/returnTo';
import PreorderSteps from './PreorderSteps';
import { createPreorderApi } from '../../services/preorderService';

/**
 * The public pre-order page: what social traffic lands on.
 *
 * Every open campaign is shown at the top and the visitor picks one, so a link
 * to the page itself sells both devices. A link that names a product
 * (/preorder/koti-ring) still works and simply starts with that one selected,
 * which is what every ad already points at.
 *
 * It renders with no account, because the visitor almost certainly does not
 * have one yet. The campaign parameters are read off the URL the moment the
 * page opens and stored with the choice, since they are gone from the address
 * bar by the time checkout finishes - without that the order could not say
 * which ad produced it.
 */
/**
 * Campaigns that have moved, old address -> current one.
 *
 * koti-cough was always a blood-pressure and ECG cuff; the name was wrong
 * from the start. Links to the old address are redirected rather than
 * broken. Orders placed before the rename keep the slug they were placed
 * under, which is what their customer actually saw.
 */
const PREORDER_SLUG_ALIASES = { 'koti-cough': 'koti-bp-ecg-cuff' };

/** Icons for the value strip, named by the content file rather than imported there. */
const VALUE_ICONS = {
  wallet: AccountBalanceWalletOutlinedIcon,
  bolt: BoltOutlinedIcon,
  shield: VerifiedUserOutlinedIcon,
  truck: LocalShippingOutlinedIcon,
  insights: MonitorHeartOutlinedIcon,
  box: Inventory2OutlinedIcon,
};

/** The notice as paragraphs, minus the heading the dialog title already shows. */
const bodyParagraphs = (doc) => {
  if (!doc || !doc.body) return [];
  const paras = doc.body.split(/\n{2,}/).map((x) => x.trim()).filter(Boolean);
  if (paras.length && doc.title && paras[0].toLowerCase() === doc.title.toLowerCase()) paras.shift();
  return paras;
};

const PreorderLanding = () => {
  const history = useHistory();
  const location = useLocation();
  const { slug } = useParams();
  const token = useSelector((state) => state.auth.token);

  /*
   * `slug` records which campaign this payload was fetched for. It is the
   * one we asked for, not the one that came back: a campaign reached by an
   * alias or an old slug answers under a different name, and comparing
   * against the reply would leave the page waiting for something that had
   * already arrived.
   */
  const [state, setState] = useState({ status: 'loading', data: null, error: '', slug: '' });
  const [plan, setPlan] = useState('');
  const [qty, setQty] = useState(1);
  // Every open campaign, for the picker. Its own request so a failure here
  // only costs the picker, never the product the visitor came for.
  const [catalogue, setCatalogue] = useState([]);
  // Which one is being shown. Seeded from the URL when it names a product.
  const [activeSlug, setActiveSlug] = useState(slug || '');
  const [imageIndex, setImageIndex] = useState(0);
  /*
   * Photo urls come from the campaign record, so one can outlive the file it
   * points at - after a rename, or a database seeded before the images were
   * added. A dead url used to leave the browser's broken-image glyph in a
   * frame collapsed to a sliver; marking it here falls back to the same
   * placeholder a campaign with no photos at all gets.
   */
  const [brokenImages, setBrokenImages] = useState({});
  /*
   * Whether the reader has moved far enough for "back to top" to mean
   * anything. <body> is the scroll container in this app, not the window,
   * so the listener goes there - window scroll events never fire.
   */
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    /*
     * How far down the page is, read from whichever element is actually
     * doing the scrolling. In this app that is <body>, so
     * document.scrollingElement - which returns <html> - reports 0 forever
     * and the button never appeared. Taking the largest of the three is
     * what scrollPageToTop already does from the other direction.
     */
    const howFar = () => Math.max(
      document.body ? document.body.scrollTop : 0,
      document.documentElement ? document.documentElement.scrollTop : 0,
      window.pageYOffset || 0,
    );
    const onScroll = () => setScrolled(howFar() > 600);
    onScroll();
    // Capture, because the scroll happens on <body> rather than on window.
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, []);
  /*
   * Someone who has asked their system for reduced motion gets the loop
   * paused on its poster, not playing. Read once, at mount: this decides the
   * video's initial state, and flipping it mid-life would restart playback
   * under the reader rather than respect them.
   *
   * Guarded because matchMedia is not everywhere - jsdom has none at all,
   * and an unguarded call takes the whole page down in the test run.
   */
  const [reduceMotion] = useState(() => {
    const mq = typeof window !== 'undefined' && window.matchMedia;
    return mq ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false;
  });
  /*
   * Starts true and is corrected by the element's own play/pause events,
   * rather than being set from what we asked for. A browser is free to
   * refuse autoplay - on a metered connection, under a power-saving mode,
   * or by the reader's own setting - and a button reading "pause" over a
   * video that never started is worse than one that catches up a frame late.
   */
  const [videoPaused, setVideoPaused] = useState(true);
  const videoRef = useRef(null);
  // The agreements used to live on a page of their own. They are here now, so
  // reserving is one screen rather than two.
  const draft = useMemo(() => readDraft(), []);
  /*
   * What they had chosen when we sent them away to sign in - the device,
   * the plan and how many. Saved on the way out and, until now, never read
   * back, so returning from the login screen reset the choice to defaults
   * and asked them to make it again.
   */
  const intent = useMemo(() => readIntent(), []);
  // Fires the resume once. A ref, not state: it must be set before the
  // await, so a re-render mid-request cannot start a second order.
  const resumed = useRef(false);
  const [consents, setConsents] = useState(() => {
    const ticked = {};
    (draft?.consents || []).forEach((c) => { if (c.granted) ticked[c.purpose] = true; });
    return ticked;
  });
  const [reading, setReading] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Captured once, on first render, before any navigation rewrites the URL.
  const attribution = useMemo(
    () => readAttribution(location.search, window.location.href),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // The catalogue, once. Also decides what to show when the URL names nothing.
  useEffect(() => {
    let cancelled = false;
    getPreorderProductsApi().then((res) => {
      if (cancelled || res.response_type === 'fail') return;
      const list = res.response?.data || [];
      setCatalogue(list);
      // No product in the URL: start on the first open one.
      setActiveSlug((current) => current || (list[0] ? list[0].slug : ''));
    });
    return () => { cancelled = true; };
  }, []);

  // A product in the URL always wins, so an ad link opens on its own product
  // even after the catalogue arrives.
  /*
   * Addresses a campaign used to live at.
   *
   * Renaming a campaign breaks every link already shared, bookmarked or
   * indexed, so the old address keeps working and quietly becomes the new
   * one. `replace`, not `push`, so Back does not bounce between the two.
   */
  useEffect(() => {
    if (!slug) return;
    const moved = PREORDER_SLUG_ALIASES[slug];
    if (moved) {
      setActiveSlug(moved);
      history.replace({ pathname: `/preorder/${moved}`, search: location.search });
      return;
    }
    setActiveSlug(slug);
  }, [slug, history, location.search]);

  useEffect(() => {
    if (!activeSlug) return undefined;
    let cancelled = false;
    setState((prev) => (prev.status === 'ready' ? prev : { status: 'loading', data: null, error: '', slug: '' }));
    getPreorderProductApi(activeSlug).then((res) => {
      if (cancelled) return;
      if (res.response_type === 'fail') {
        setState({
          status: 'error', data: null, slug: activeSlug,
          error: res.response?.message || strings.preorderNotAvailable,
        });
        return;
      }
      const data = res.response?.data;
      setState({ status: 'ready', data, error: '', slug: activeSlug });
      // Default to the plan the business leads with, which is paying in full.
      const plans = data?.product?.plans || [];
      /*
       * Their own choice wins if they are coming back from signing in and
       * the campaign still offers it; otherwise the plan the business
       * leads with, which is paying in full.
       */
      const resuming = intent && intent.slug === activeSlug;
      const wanted = resuming && plans.find((p) => p.code === intent.paymentPlan);
      const preferred = wanted || plans.find((p) => p.code === 'PREPAID') || plans[0];
      setPlan(preferred ? preferred.code : '');
      // Quantity and the chosen photo belong to the product that was on
      // screen when they were set - unless they are returning to finish a
      // reservation, in which case it is the quantity they chose.
      setQty(resuming && intent.qty > 0 ? intent.qty : 1);
      setImageIndex(0);
    });
    return () => { cancelled = true; };
  }, [activeSlug]);

  /**
   * Switching product rewrites the address bar so the page can be shared or
   * reloaded on the one being looked at. `replace`, not `push`, so Back still
   * leaves the pre-order rather than walking through every product tried.
   */
  const onPickProduct = (nextSlug) => {
    if (nextSlug === activeSlug) return;
    setActiveSlug(nextSlug);
    history.replace({ pathname: `/preorder/${nextSlug}`, search: location.search });
    /*
     * Straight to the top, so the wait for the new product is spent at the
     * top of the page rather than halfway down the old one. The layout
     * effect above repeats this once the new product has rendered, which is
     * the one that actually guarantees it; this is only so the jump happens
     * on the click rather than a request later.
     *
     * Jumped, not smooth-scrolled: the content underneath is being replaced
     * at the same time, and animating past sections that are changing as
     * they pass reads as a glitch rather than as movement.
     */
    scrollPageToTop();
  };

  const data = state.data;
  const product = data?.product;

  /*
   * True between tapping another device and its data arriving.
   *
   * The fetch above deliberately leaves the previous product mounted while
   * the next one loads - that is what stops a refresh flashing an empty
   * page - but it also meant a tap looked like it had done nothing, and
   * then the price, the stock and the dispatch window changed under the
   * reader with no warning that anything had happened.
   *
   * Compares the slug this payload was fetched for against the one now
   * selected, so it is true for exactly as long as the request is in
   * flight, and false again the moment that request settles either way.
   */
  const switching = state.status === 'ready' && state.slug !== activeSlug;

  /*
   * A product switch is a page change, so it starts at the top.
   *
   * This is done here rather than where the switch is asked for, because at
   * that moment the old product is still the one on screen: the new one
   * arrives a request later, and a scroll set against the outgoing page does
   * not reliably survive the swap. Keyed on the product that has actually
   * rendered, it cannot run too early.
   *
   * Before paint, so the new product is never briefly visible at the old
   * page's scroll position. The first product is left alone - the visitor
   * either arrived at the top, or reloaded part-way down a page they were
   * reading and should stay where they were.
   */
  const shownSlug = useRef('');
  useLayoutEffect(() => {
    const next = product?.slug;
    if (!next) return;
    const previous = shownSlug.current;
    shownSlug.current = next;
    if (!previous || previous === next) return;
    scrollPageToTop();
  }, [product]);
  const selected = (product?.plans || []).find((p) => p.code === plan);
  const maxQty = product?.maxQtyPerUser || 2;
  const allPlans = product?.plans || [];
  const singlePlan = allPlans.length === 1 ? allPlans[0] : null;

  const legal = data?.legal || {};
  const purposes = data?.consents || [];
  const allAgreed = purposes.every((c) => !c.required || consents[c.purpose]);
  const mixedConsents = purposes.some((c) => !c.required);

  const markBroken = (src) => setBrokenImages((prev) => ({ ...prev, [src]: true }));

  /*
   * The page's copy.
   *
   * The campaign record wins, section by section. Whatever the API sends in
   * `content` is laid over the copy bundled with the page, so a wording
   * change is a content edit and a seed run - no frontend build, no deploy.
   * The bundled copy stays underneath as the fallback, which is what keeps
   * the page working for a campaign that has no content of its own, and
   * what it falls back to if the field is ever cleared.
   *
   * Shallow on purpose: a section is taken from the record whole or not at
   * all. Merging inside `seo` or `keyBenefits` would let a half-written
   * record produce a page that is neither the old copy nor the new one.
   *
   * Every section below reads its own key defensively. The copy for one
   * campaign is not obliged to carry the same sections as another - Koti
   * Ring's approved copy has no "built different" list, for instance - and
   * a key simply absent should drop that section, not take the page down.
   */
  const demo = useMemo(
    () => ({ ...demoFor(activeSlug), ...(product && product.content ? product.content : {}) }),
    [activeSlug, product],
  );

  /*
   * The photographs.
   *
   * The campaign record wins whenever it carries real ones. A record that
   * still points at a stand-in graphic, or at a file that has since moved,
   * falls back to the shots bundled with the page, so the gallery is never
   * empty or broken while the database catches up. Nothing here is sent
   * anywhere: it decides what is displayed, and that is all.
   */
  const usable = (src) => src && !/placeholder/i.test(src) && !brokenImages[src];
  const campaignImages = (product?.images || []).filter(usable);
  const images = campaignImages.length ? campaignImages : (demo.gallery || []).filter(usable);
  const activeImage = images[Math.min(imageIndex, Math.max(images.length - 1, 0))];
  const shownImage = activeImage || '';
  /*
   * The supplied marketing panels, which are not part of the gallery and are
   * never overridden by the campaign record - they belong to the page's copy,
   * not to the product. Same broken-image guard as everything else here: a
   * panel that will not load leaves the band rather than showing a torn
   * frame, and a band left with nothing does not render at all.
   */
  const panels = (demo.showcase || []).filter((p) => p.image && !brokenImages[p.image]);

  /*
   * The ambient loop. Same guard again: a file that is not on disk yet - and
   * none of them are, until the footage is shot - fires an error and the
   * frame leaves the page rather than sitting there empty.
   */
  /*
   * The two calls to action from the approved copy.
   *
   * {price} is filled from the campaign record rather than written into
   * the content file, so the button can never advertise a price the page
   * is no longer selling at. A campaign with no `cta` block falls back to
   * the generic wording, which is what every other campaign still uses.
   */
  const ctaCopy = demo.cta || null;
  const fillCta = (template, name, paise) => template
    .replace('{name}', name || '')
    .replace('{price}', formatPaise(paise || 0));
  const reserveLabel = ctaCopy && ctaCopy.primary && product
    ? fillCta(ctaCopy.primary, ctaCopy.name || product.title, product.price)
    : strings.reserveYourUnit;
  /*
   * The secondary call to action, which comes in two shapes.
   *
   * `slug` points at another campaign - shown only while that campaign is
   * actually on sale, so nothing ever offers a route to something that
   * cannot be reserved. `anchor` points at a section of this page, which
   * is what the package uses for "How the Duo works", and is shown only if
   * that section is really on the page.
   */
  const secondaryCta = (() => {
    const sec = ctaCopy && ctaCopy.secondary;
    if (!sec) return null;
    if (sec.anchor) {
      return { label: fillCta(sec.label, product && product.title, product && product.price), anchor: sec.anchor };
    }
    const target = catalogue.find((c) => c.slug === sec.slug);
    if (!target || !target.inStock) return null;
    return { label: fillCta(sec.label, target.title, target.price), slug: target.slug };
  })();

  /*
   * Jumping to a section of this page. `scrollIntoView` rather than the
   * page's own scroll helper: that one exists because window.scrollTo does
   * nothing here - <body> is the scroll container - and scrollIntoView is
   * the one API that does not care which ancestor scrolls.
   */
  const goToSection = (anchor) => {
    const target = document.getElementById(anchor);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const loop = demo.video && !brokenImages[demo.video.src] ? demo.video : null;
  const toggleLoop = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      // Autoplay can be refused by the browser whatever we ask for, so the
      // button follows what the element actually did, not what we wanted.
      // play() predates promises and still returns undefined in older
      // browsers and in jsdom, hence the check before chaining onto it.
      const started = el.play();
      if (started && started.then) {
        started.then(() => setVideoPaused(false)).catch(() => setVideoPaused(true));
      } else {
        setVideoPaused(false);
      }
    } else {
      el.pause();
      setVideoPaused(true);
    }
  };
  /*
   * Packages.
   *
   * A package is an ordinary campaign that ships more than one device, so
   * the whole page already works for it; these two lines are the only thing
   * it needs on top. `contents` is what is inside the one being shown, and
   * `inPackage` is a package that contains the device being shown - found in
   * the catalogue that is already loaded for the picker, so neither costs a
   * request. Both are empty for a single device with no package, and the
   * sections that use them render nothing at all in that case.
   */
  const contents = product?.bundleItems || [];
  const inPackage = catalogue.find(
    (item) => item.slug !== activeSlug
      && (item.bundleItems || []).some((part) => part.slug === activeSlug)
  );
  /**
   * A campaign that ships more than one device is a package, and the package
   * is the option the picker recommends. Derived from the record rather than
   * named by slug, so a second package needs no change here.
   */
  const isPackage = (item) => Boolean((item.bundleItems || []).length);
  /** Real money off, taken from the campaign - never a figure typed into the page. */
  const savingOn = (item) => (item && item.compareAtPrice > item.price
    ? item.compareAtPrice - item.price
    : 0);
  /** A picture for a campaign we know only by slug, from what is already loaded. */
  const shotFor = (name) => {
    const entry = catalogue.find((c) => c.slug === name);
    return [...(entry?.images || []), ...(demoFor(name).gallery || [])].filter(usable)[0] || '';
  };

  /*
   * One rule for the reserve action, so the panel and the bar pinned to the
   * bottom of the window can never disagree about whether it is available.
   */
  const canReserve = Boolean(data?.isOpen && plan && allAgreed && !busy);

  /** What the server stores as proof: the version and hash it served us. */
  const consentPayload = () => purposes.map((entry) => {
    const copy = CONSENT_COPY[entry.purpose] || {};
    const doc = legal[copy.documentType] || {};
    return {
      purpose: entry.purpose,
      granted: Boolean(consents[entry.purpose]),
      documentType: copy.documentType,
      noticeVersion: doc.version,
      noticeTextHash: doc.contentHash,
    };
  });

  /**
   * Not signed in yet: keep what they chose and send them to create an
   * account. The order is placed for them the moment they confirm the code,
   * so none of this is asked twice.
   */
  const onContinueToSignIn = () => {
    saveIntent({ slug: activeSlug, paymentPlan: plan, qty, attribution });
    saveDraft({
      slug: activeSlug,
      paymentPlan: plan,
      qty,
      consents: consentPayload(),
      attribution,
    });
    setReturnTo(`/preorder/${activeSlug}`);
    history.push(`/preorder/${activeSlug}/login`);
  };

  /*
   * Finish the reservation they had already started.
   *
   * Clicking Reserve while signed out saves the choice and sends them to
   * sign in. They came back to this page and had to find and press the
   * same button again, having already agreed to everything - so the
   * reservation completes itself and they land on their orders with the
   * confirmation, which is exactly what a signed-in customer sees.
   *
   * Deliberately narrow. It runs only when there is a saved intent for
   * this campaign, only when the page is otherwise ready to reserve -
   * same `canReserve` gate as the button, so the required agreements must
   * genuinely have been restored - and only once. If anything is missing
   * it does nothing and the pre-filled page is there to press.
   */
  useEffect(() => {
    if (resumed.current) return;
    if (!token || !intent || intent.slug !== activeSlug) return;
    if (!canReserve) return;
    resumed.current = true;
    placeOrder({ paymentPlan: intent.paymentPlan || plan, quantity: intent.qty || qty });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, intent, activeSlug, canReserve]);

  /*
   * Places the order. The plan and quantity are arguments rather than read
   * from state, because the resume above has to send exactly what they
   * chose before signing in - reading state there made it a race with the
   * render that restores it, and it quietly reserved one unit instead of
   * two.
   */
  const placeOrder = async ({ paymentPlan, quantity }) => {
    setSubmitError('');
    setBusy(true);
    const res = await createPreorderApi({
      token,
      slug: activeSlug,
      paymentPlan,
      qty: quantity,
      consents: consentPayload(),
      attribution,
    });
    if (res.response_type === 'fail') {
      setBusy(false);
      setSubmitError(res.response?.message || strings.preorderGenericError);
      return;
    }
    const { preorder } = res.response?.data || {};
    clearIntent();
    clearDraft();
    setBusy(false);
    // Same confirmation as the signup path, so reserving looks the same
    // whether or not they already had an account.
    markJustPlaced(preorder.orderNumber);
    history.push('/patient/preorders');
  };

  const onReserve = () => {
    if (!token) {
      onContinueToSignIn();
      return;
    }
    return placeOrder({ paymentPlan: plan, quantity: qty });
  };

  /*
   * Hoisted so the same picker renders in two places: the page proper, and
   * the waiting state while another device loads. It has to stay on screen
   * and stay live through the wait - the reader has just tapped it, and the
   * tap is only believable if the choice it made is visible.
   */
  const picker = catalogue.length > 1 ? (
    <ProductPicker role="radiogroup" aria-label={strings.choosePreorderProduct}>
      {catalogue.map((item) => {
        const isActive = item.slug === activeSlug;
        return (
          <ProductTab
            key={item.id}
            type="button"
            role="radio"
            aria-checked={isActive}
            data-selected={isActive ? 'true' : 'false'}
            data-recommended={isPackage(item) ? 'true' : 'false'}
            onClick={() => onPickProduct(item.slug)}
          >
            {isPackage(item) ? (
              <span className="recommended">{strings.recommendedTag}</span>
            ) : null}
            <span className="name">{item.title}</span>
            <span className="price">{formatPaise(item.price)}</span>
            {!item.inStock ? <span className="sold-out">{strings.preorderSoldOut}</span> : null}
            {savingOn(item) ? (
              <span className="save">
                {strings.save} {formatPaise(savingOn(item))}
              </span>
            ) : null}
          </ProductTab>
        );
      })}
    </ProductPicker>
  ) : null;

  /** The spinner itself, so the first load and a switch cannot drift apart. */
  const waiting = (
    <CenterState data-busy="true" role="status" aria-live="polite">
      <CircularProgress size={34} thickness={4} sx={{ color: '#1AA19C' }} />
      <div>{strings.loadingProduct}</div>
    </CenterState>
  );

  if (state.status === 'loading' || !activeSlug) {
    return <Page>{waiting}</Page>;
  }
  if (state.status === 'error') {
    return (
      <Page>
        <CenterState>
          {state.error}
          <div style={{ marginTop: 20 }}>
            <OutlineButton onClick={() => history.push('/')}>{strings.backToHome}</OutlineButton>
          </div>
        </CenterState>
      </Page>
    );
  }

  /*
   * Another device is on its way. The picker above stays live so the new
   * choice reads as taken, and everything below it waits - rather than
   * leaving the previous device's price, stock and dispatch window sitting
   * under a selection that no longer matches them, which on a page that
   * takes orders is worse than showing nothing.
   */
  if (switching) {
    return (
      <Page>
        <PreorderSteps current={0} hidden={Boolean(token)} />
        {picker}
        {waiting}
      </Page>
    );
  }

  return (
    <Page data-sticky={data.isOpen ? 'true' : 'false'}>
      {/* Only where the copy has been written and signed off. A campaign
          without it keeps the site-wide tags rather than getting invented
          ones. */}
      {demo.seo ? (
        <Helmet>
          <title>{demo.seo.title}</title>
          <meta name="description" content={demo.seo.description} />
          <meta name="keywords" content={demo.seo.keywords} />
          <meta property="og:title" content={demo.seo.ogTitle} />
          <meta property="og:description" content={demo.seo.ogDescription} />
          <meta property="og:type" content="product" />
          {demo.seo.ogImage ? <meta property="og:image" content={demo.seo.ogImage} /> : null}
        </Helmet>
      ) : null}
      {/* A patient who is already signed in reserves in one action, so there
          is no journey to show them. */}
      <PreorderSteps current={0} hidden={Boolean(token)} />

      {picker}

      <Hero>
        <GalleryPane data-rail={images.length > 1 ? 'true' : 'false'}>
          {/* A rail beside the photograph on desktop, a row under it on a
              phone. Only worth showing when there is more than one shot. */}
          {images.length > 1 ? (
            <ThumbRail>
              {images.map((src, index) => (
                <GalleryThumb
                  key={src}
                  type="button"
                  data-active={index === imageIndex ? 'true' : 'false'}
                  aria-label={`${product.title} photo ${index + 1}`}
                  onClick={() => setImageIndex(index)}
                >
                  {brokenImages[src]
                    ? <ShoppingBagOutlinedIcon sx={{ color: '#cfcfd7', fontSize: 22 }} />
                    : <img src={src} alt="" onError={() => markBroken(src)} />}
                </GalleryThumb>
              ))}
            </ThumbRail>
          ) : null}

          <Gallery data-empty={shownImage ? 'false' : 'true'}>
            {shownImage
              ? (
                <img
                  src={shownImage}
                  alt={product.title}
                  onError={() => markBroken(shownImage)}
                />
              )
              : (
                <>
                  <ShoppingBagOutlinedIcon sx={{ color: '#CFCFD7', fontSize: 52 }} />
                  <span className="caption">{strings.photoComingSoon}</span>
                </>
              )}
          </Gallery>
        </GalleryPane>

        <BuyBox>
          {/* The page's headline where the copy gives one, otherwise the
              product's name. Only the heading changes: the picker, the
              pinned bar and the order all keep the short name. */}
          <Title>{demo.headline || product.title}</Title>
          {demo.tagline ? <Tagline>{demo.tagline}</Tagline> : null}

          <Price>
            {formatPaise(product.price)}
            {/* Shown only when the campaign carries a real comparison price.
                A struck-through figure is a claim about what the thing
                usually costs, so it is never invented here. */}
            {product.compareAtPrice > product.price ? (
              <>
                <Mrp>{formatPaise(product.compareAtPrice)}</Mrp>
                <SaveTag>
                  {strings.youSave}
                  {' '}
                  {Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)}%
                </SaveTag>
              </>
            ) : null}
          </Price>
          <Muted>{strings.inclusiveOfTaxes}</Muted>
          {/* What the product legally is. Next to the price because that is
              where someone decides, not buried at the foot of the page. */}
          {demo.productClass ? <ProductClass>{demo.productClass}</ProductClass> : null}

          <StockPill data-tone={data.isOpen && product.inStock ? 'good' : 'bad'}>
            {!data.isOpen || !product.inStock
              ? strings.preorderSoldOut
              : strings.limitedFirstBatch}
          </StockPill>

          <DispatchBadge>
            <LocalShippingOutlinedIcon sx={{ fontSize: 18 }} />
            {strings.expectedDispatch}: {formatWindow(product.expectedShipFrom, product.expectedShipTo)}
          </DispatchBadge>
          {/* Said plainly and up front, not buried in the terms. */}
          <Muted>{strings.dispatchEstimateNote}</Muted>

          {product.highlights && product.highlights.length ? (
            <Highlights>
              {product.highlights.map((item) => <li key={item}>{item}</li>)}
            </Highlights>
          ) : null}

          {!data.isOpen ? (
            <Notice data-tone="error">
              {product.inStock ? strings.preorderClosed : strings.preorderSoldOut}
            </Notice>
          ) : (
            <>
              {/* With a single plan there is nothing to choose, so it is stated
                  rather than offered. The chooser only appears if a campaign
                  brings back the prepaid or deposit options. */}
              {singlePlan ? (
                <>
                  <SectionTitle style={{ marginTop: 28 }}>
                    {singlePlan.dueNow === 0 ? strings.noPaymentToday : PLAN_COPY[singlePlan.code]?.title}
                  </SectionTitle>
                  <PlanCard data-selected="true" style={{ cursor: 'default' }}>
                    <PlanTop>
                      <PlanTitle>{strings.dueToday}</PlanTitle>
                      <PlanAmount>{formatPaise(singlePlan.dueNow)}</PlanAmount>
                    </PlanTop>
                    <Muted style={{ marginTop: 8 }}>
                      {singlePlan.dueNow === 0 ? strings.noPaymentTodayNote : PLAN_COPY[singlePlan.code]?.note}
                    </Muted>
                    {singlePlan.dueLater > 0 ? (
                      <Muted style={{ marginTop: 8, fontWeight: 500, color: '#4A4A5A' }}>
                        {strings.dueLater}: {formatPaise(singlePlan.dueLater)} &middot; {strings.paymentCollectedBefore}
                      </Muted>
                    ) : null}
                  </PlanCard>
                </>
              ) : null}

              {!singlePlan ? (
              <>
              <SectionTitle style={{ marginTop: 28 }}>{strings.choosePaymentOption}</SectionTitle>
              <PlanList>
                {(product.plans || []).map((option) => {
                  const copy = PLAN_COPY[option.code] || { title: option.code, note: '' };
                  const isSelected = option.code === plan;
                  return (
                    <PlanCard
                      key={option.code}
                      data-selected={isSelected ? 'true' : 'false'}
                      onClick={() => setPlan(option.code)}
                      role="radio"
                      aria-checked={isSelected}
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setPlan(option.code); }}
                    >
                      <PlanTop>
                        <PlanTitle>
                          {copy.title}
                          {option.discountPct > 0 ? (
                            <SaveTag style={{ marginLeft: 8 }}>
                              {strings.youSave} {option.discountPct}%
                            </SaveTag>
                          ) : null}
                        </PlanTitle>
                        <PlanAmount>
                          {option.dueNow > 0
                            ? `${formatPaise(option.dueNow)} ${strings.dueToday.toLowerCase()}`
                            : strings.dueToday + ': ' + formatPaise(0)}
                        </PlanAmount>
                      </PlanTop>
                      <Muted style={{ marginTop: 6 }}>{copy.note}</Muted>
                      {option.dueLater > 0 ? (
                        <Muted style={{ marginTop: 4 }}>
                          {strings.dueLater}: {formatPaise(option.dueLater)}
                        </Muted>
                      ) : null}
                    </PlanCard>
                  );
                })}
              </PlanList>
              </>
              ) : null}

              <div style={{
                display: 'flex', alignItems: 'flex-end', gap: 16,
                flexWrap: 'wrap', marginTop: 22,
              }}
              >
                <div>
                  <Muted style={{ marginBottom: 6 }}>{strings.quantity}</Muted>
                  <QtyBox>
                    <button
                      type="button"
                      aria-label={strings.decreaseQuantity}
                      disabled={qty <= 1}
                      onClick={() => setQty((n) => Math.max(1, n - 1))}
                    >
                      &minus;
                    </button>
                    <span className="count">{qty}</span>
                    <button
                      type="button"
                      aria-label={strings.increaseQuantity}
                      disabled={qty >= maxQty}
                      onClick={() => setQty((n) => Math.min(maxQty, n + 1))}
                    >
                      +
                    </button>
                  </QtyBox>
                </div>
                {selected ? (
                  <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                    <Muted>{selected.dueNow === 0 ? strings.nothingToPayNow : strings.dueToday}</Muted>
                    <div style={{ fontSize: 22, fontWeight: 600, color: '#1AA19C' }}>
                      {formatPaise(selected.dueNow * qty)}
                    </div>
                    {selected.dueLater > 0 ? (
                      <Muted style={{ marginTop: 2 }}>
                        {strings.dueLater} {formatPaise(selected.dueLater * qty)}
                      </Muted>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {/* The agreements, on the same screen as the thing being
                  agreed to. None pre-ticked, one box per purpose. */}
              {purposes.length ? (
                <div style={{ marginTop: 18, borderTop: '1px solid #F2F2F7', paddingTop: 6 }}>
                  {purposes.map((entry) => {
                    const copy = CONSENT_COPY[entry.purpose];
                    if (!copy) return null;
                    const doc = legal[copy.documentType];
                    return (
                      <ConsentRow key={entry.purpose}>
                        <input
                          type="checkbox"
                          checked={Boolean(consents[entry.purpose])}
                          onChange={(e) => setConsents({ ...consents, [entry.purpose]: e.target.checked })}
                        />
                        <span>
                          {copy.label}
                          {mixedConsents ? (
                            <ConsentTag data-required={entry.required ? 'true' : 'false'}>
                              {entry.required ? strings.requiredConsent : strings.optionalConsent}
                            </ConsentTag>
                          ) : null}
                          {doc ? (
                            <>
                              {' '}
                              <button
                                type="button"
                                onClick={(e) => { e.preventDefault(); setReading(doc); }}
                                style={{
                                  background: 'none', border: 0, cursor: 'pointer',
                                  color: '#097EB7', fontSize: 13, textDecoration: 'underline',
                                  fontFamily: 'inherit',
                                  // A bigger thumb target at no cost to the layout: the padding
                                  // grows the hit area, the matching negative margin keeps the
                                  // sentence it sits inside spaced exactly as before.
                                  padding: '8px 3px', margin: '-8px -3px',
                                }}
                              >
                                {strings.readFullText}
                              </button>
                            </>
                          ) : null}
                        </span>
                      </ConsentRow>
                    );
                  })}
                </div>
              ) : null}

              {submitError ? <Notice data-tone="error">{submitError}</Notice> : null}

              <div style={{ marginTop: 18 }}>
                <PrimaryButton disabled={!canReserve} onClick={onReserve} fullWidth>
                  {busy ? strings.loading : reserveLabel}
                </PrimaryButton>
              </div>
              {/* Section 1's secondary call to action: the package, priced
                  from its own campaign rather than from a number typed
                  into the copy. */}
              {secondaryCta ? (
                <div style={{ marginTop: 10 }}>
                  <OutlineButton
                    fullWidth
                    onClick={() => (secondaryCta.anchor
                      ? goToSection(secondaryCta.anchor)
                      : onPickProduct(secondaryCta.slug))}
                  >
                    {secondaryCta.label}
                  </OutlineButton>
                </div>
              ) : null}
              {!allAgreed ? (
                <Muted style={{ marginTop: 10 }}>{strings.acceptAllToContinue}</Muted>
              ) : !token ? (
                <Muted style={{ marginTop: 10 }}>{strings.accountNeededNote}</Muted>
              ) : null}

              <TrustRow>
                <div>
                  <LockOutlinedIcon sx={{ fontSize: 20 }} />
                  <span>{strings.secureCheckout}</span>
                </div>
                <div>
                  <VerifiedUserOutlinedIcon sx={{ fontSize: 20 }} />
                  <span>{strings.oneYearWarranty}</span>
                </div>
                <div>
                  <LocalShippingOutlinedIcon sx={{ fontSize: 20 }} />
                  <span>{strings.freeDelivery}</span>
                </div>
                <div>
                  <CheckCircleOutlineIcon sx={{ fontSize: 20 }} />
                  <span>{strings.cancelBeforePaying}</span>
                </div>
              </TrustRow>
            </>
          )}
        </BuyBox>
      </Hero>

      {/*
        * A package says what is in it, and what those devices cost on their
        * own. Both figures come from the campaign record - the page never
        * works out a saving of its own - so a package with no comparison
        * price simply lists its contents without claiming anything.
        */}
      {contents.length ? (
        <Card style={{ marginTop: 40 }}>
          <SectionTitle>{strings.packageContentsTitle}</SectionTitle>
          <Muted style={{ marginBottom: 18 }}>{strings.packageContentsNote}</Muted>
          <PackageGrid>
            {contents.map((part) => {
              const shot = shotFor(part.slug);
              return (
                <PackageCard key={part.sku}>
                  <div className="shot">
                    {shot
                      ? <img src={shot} alt="" onError={() => markBroken(shot)} />
                      : <ShoppingBagOutlinedIcon sx={{ fontSize: 28 }} />}
                  </div>
                  <div>
                    <div className="name">
                      {part.qty > 1 ? `${part.qty} x ` : ''}{part.title}
                    </div>
                    <div className="alone">
                      {strings.boughtSeparately}: {formatPaise(part.price)}
                    </div>
                    {/* A real url, so it can be opened in a tab, but handled
                        in the app so a click does not reload the page. */}
                    {part.slug ? (
                      <a
                        href={`/preorder/${part.slug}`}
                        onClick={(e) => { e.preventDefault(); onPickProduct(part.slug); }}
                      >
                        {strings.viewThisDevice}
                      </a>
                    ) : null}
                  </div>
                </PackageCard>
              );
            })}
          </PackageGrid>

          {savingOn(product) ? (
            <PackageSummary>
              <span className="label">{strings.boughtSeparately}</span>
              <span className="struck">{formatPaise(product.compareAtPrice)}</span>
              <span className="saving">
                {strings.youSaveAmount} {formatPaise(savingOn(product))}
              </span>
            </PackageSummary>
          ) : null}
        </Card>
      ) : null}

      {/*
        * On a device's own page: the package containing it, stated once and
        * quietly. Reserving the single device stays exactly where it was.
        */}
      {!contents.length && inPackage ? (
        <CrossSell>
          <div>
            <div className="title">{strings.alsoInPackageTitle}</div>
            <div className="note">
              {inPackage.title} &middot; {formatPaise(inPackage.price)}
              {savingOn(inPackage)
                ? ` · ${strings.youSaveAmount} ${formatPaise(savingOn(inPackage))}`
                : ''}
            </div>
          </div>
          <OutlineButton onClick={() => onPickProduct(inPackage.slug)}>
            {strings.seeThePackage}
          </OutlineButton>
        </CrossSell>
      ) : null}

      <Dialog
        open={Boolean(reading)}
        onClose={() => setReading(null)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '78vh' } }}
      >
        <DialogTitle sx={{ fontFamily: 'Poppins, sans-serif', fontSize: 18, fontWeight: 600, pb: 1 }}>
          {reading?.title}
          <Muted style={{ marginTop: 2, fontSize: 12 }}>
            {strings.versionLabel} {reading?.version}
          </Muted>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
          {bodyParagraphs(reading).map((para, i) => (
            <p
              key={i}
              style={{
                fontFamily: 'Poppins, sans-serif', fontSize: 14, lineHeight: 1.75,
                color: '#4A4A5A', margin: '0 0 14px',
              }}
            >
              {para}
            </p>
          ))}
          {reading?.url ? (
            <a href={reading.url} target="_blank" rel="noreferrer" style={{ color: '#097EB7', fontSize: 13 }}>
              {strings.openFullPage}
            </a>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <OutlineButton onClick={() => setReading(null)}>{strings.close}</OutlineButton>
        </DialogActions>
      </Dialog>

      {product.description ? (
        <Card style={{ marginTop: 40 }}>
          <SectionTitle>{strings.productDetails}</SectionTitle>
          <div style={{ fontSize: 14, lineHeight: 1.8, color: '#4A4A5A', whiteSpace: 'pre-line' }}>
            {product.description}
          </div>
        </Card>
      ) : null}

      {/*
        * Everything from here down is placeholder copy from demoContent.js.
        * It is presentation only - no part of it reaches the order - and each
        * section disappears entirely when its campaign has nothing to say, so
        * a product with no copy yet simply ends above this line.
        */}
      {/* The case for the package, above the case for pre-ordering: whether
          to take both devices is the question a visitor on this page has
          not already answered. Only a package has any. */}
      {contents.length && (demo.whyPackage || []).length ? (
        <>
          <SectionHead>
            <h2>{strings.whyPackageTitle}</h2>
            <p>{demo.tagline}</p>
          </SectionHead>
          <ValueStrip>
            {demo.whyPackage.map((item) => {
              const Icon = VALUE_ICONS[item.icon] || CheckCircleOutlineIcon;
              return (
                <ValueCard key={item.title}>
                  <Icon sx={{ fontSize: 26 }} />
                  <div className="title">{item.title}</div>
                  <div className="note">{item.note}</div>
                </ValueCard>
              );
            })}
          </ValueStrip>
        </>
      ) : null}

      {/* The product's own case, before the case for pre-ordering it. Both
          are card strips, so they reuse the strip the package already has. */}
      {demo.keyBenefits && (demo.keyBenefits.items || []).length ? (
        <>
          <SectionHead>
            <h2>{demo.keyBenefits.title}</h2>
            {demo.keyBenefits.note ? <p>{demo.keyBenefits.note}</p> : null}
          </SectionHead>
          <ValueStrip>
            {demo.keyBenefits.items.map((item) => {
              const Icon = VALUE_ICONS[item.icon] || CheckCircleOutlineIcon;
              return (
                <ValueCard key={item.title}>
                  <Icon sx={{ fontSize: 26 }} />
                  <div className="title">{item.title}</div>
                  <div className="note">{item.note}</div>
                </ValueCard>
              );
            })}
          </ValueStrip>
        </>
      ) : null}

      {demo.platform && (demo.platform.items || []).length ? (
        <>
          <SectionHead>
            <h2>{demo.platform.title}</h2>
            <p>{demo.platform.note}</p>
          </SectionHead>
          <ValueStrip>
            {demo.platform.items.map((item) => {
              const Icon = VALUE_ICONS[item.icon] || CheckCircleOutlineIcon;
              return (
                <ValueCard key={item.title}>
                  <Icon sx={{ fontSize: 26 }} />
                  <div className="title">{item.title}</div>
                  <div className="note">{item.note}</div>
                </ValueCard>
              );
            })}
          </ValueStrip>
        </>
      ) : null}

      {(demo.valueProps || []).length ? (
        <>
          <SectionHead>
            <h2>{strings.whyPreorderTitle}</h2>
            <p>{strings.whyPreorderNote}</p>
          </SectionHead>
          <ValueStrip>
            {demo.valueProps.map((item) => {
              const Icon = VALUE_ICONS[item.icon] || CheckCircleOutlineIcon;
              return (
                <ValueCard key={item.title}>
                  <Icon sx={{ fontSize: 26 }} />
                  <div className="title">{item.title}</div>
                  <div className="note">{item.note}</div>
                </ValueCard>
              );
            })}
          </ValueStrip>
        </>
      ) : null}

      {/* Named so a secondary call to action can point at it - this is the
          section that answers how the devices work together. */}
      {(demo.features || []).length ? (
        <div id="how-it-works" style={{ marginTop: 56, scrollMarginTop: 101 }}>
          {demo.features.map((feature, index) => {
            // Same guard as the gallery: a shot that will not load drops out
            // and the row lays itself out as a text-only one, rather than
            // keeping a column for the browser's broken-image glyph.
            const shot = feature.image && !brokenImages[feature.image] ? feature.image : '';
            return (
            <FeatureRow
              key={feature.title}
              data-flip={index % 2 === 1 ? 'true' : 'false'}
              data-solo={shot ? 'false' : 'true'}
            >
              {shot ? (
                <div className="shot">
                  <img src={shot} alt="" onError={() => markBroken(shot)} />
                </div>
              ) : null}
              <div className="copy">
                <h3>{feature.title}</h3>
                <p>{feature.body}</p>
              </div>
            </FeatureRow>
            );
          })}
        </div>
      ) : null}

      {/* Shown whole and uncropped: these are composed posters, and the
          heading above them is the only thing this page adds. They come
          after the feature rows deliberately - the rows make the argument in
          words, and these are the same argument in pictures. */}
      {loop || panels.length ? (
        <>
          <SectionHead>
            <h2>{strings.closerLookTitle}</h2>
            <p>{strings.closerLookNote}</p>
          </SectionHead>
          {loop ? (
            <VideoFrame>
              {/* Muted and inline, which is what every mobile browser
                  requires before it will autoplay anything at all. */}
              <video
                ref={videoRef}
                src={loop.src}
                poster={loop.poster || undefined}
                aria-label={strings.productLoopLabel}
                muted
                loop
                playsInline
                preload="metadata"
                autoPlay={!reduceMotion}
                onError={() => markBroken(loop.src)}
                onPlay={() => setVideoPaused(false)}
                onPause={() => setVideoPaused(true)}
              />
              <button
                type="button"
                onClick={toggleLoop}
                aria-label={videoPaused ? strings.playVideo : strings.pauseVideo}
              >
                {videoPaused
                  ? <PlayArrowRoundedIcon sx={{ fontSize: 22 }} />
                  : <PauseRoundedIcon sx={{ fontSize: 22 }} />}
              </button>
            </VideoFrame>
          ) : null}
          {panels.length ? (
          <PosterGrid>
            {panels.map((panel) => (
              <figure key={panel.image}>
                <img
                  src={panel.image}
                  alt={panel.alt}
                  loading="lazy"
                  onError={() => markBroken(panel.image)}
                />
              </figure>
            ))}
          </PosterGrid>
          ) : null}
        </>
      ) : null}

      {(demo.builtDifferent || []).length ? (
        <>
          <SectionHead>
            <h2>{strings.builtDifferentTitle}</h2>
            <p>{strings.builtDifferentNote}</p>
          </SectionHead>
          <TagGrid>
            {demo.builtDifferent.map((label) => (
              <TagItem key={label}>
                <CheckCircleOutlineIcon sx={{ fontSize: 19 }} />
                {label}
              </TagItem>
            ))}
          </TagGrid>
        </>
      ) : null}

      {(demo.faqs || []).length ? (
        <>
          <SectionHead>
            <h2>{strings.faqTitle}</h2>
            <p>{strings.faqNote}</p>
          </SectionHead>
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            {demo.faqs.map((item) => (
              <FaqItem key={item.q}>
                <summary>{item.q}</summary>
                <div className="answer">{item.a}</div>
              </FaqItem>
            ))}
          </div>
        </>
      ) : null}

      {scrolled ? (
        <ToTopButton
          type="button"
          aria-label={strings.backToTop}
          data-bar={data.isOpen ? 'true' : 'false'}
          onClick={scrollPageToTop}
        >
          <KeyboardArrowUpRoundedIcon sx={{ fontSize: 24 }} />
        </ToTopButton>
      ) : null}

      {/* The panel above is off screen for most of this page, and everything
          on the way down is a reason to reserve with no way to do it. */}
      {data.isOpen ? (
        <StickyBuyBar>
          <div className="what">
            <div className="name">{product.title}</div>
            <div className="cost">
              {selected && selected.dueNow > 0
                ? `${strings.dueToday}: ${formatPaise(selected.dueNow * qty)}`
                : `${formatPaise(product.price * qty)} · ${strings.nothingToPayNow}`}
            </div>
          </div>
          <PrimaryButton disabled={!canReserve} onClick={onReserve}>
            {busy ? strings.loading : strings.reserveYourUnit}
          </PrimaryButton>
        </StickyBuyBar>
      ) : null}
    </Page>
  );
};

export default PreorderLanding;
